import { useEffect } from 'react';
import { reaction, transaction } from 'mobx';
import { HeroThreeAnimationBridge as Bridge } from '../bridge';
import { SrcSetArgs, createLazyVideoTexture } from 'scripts/animations/lazy-video';
import { BreakpointType, Breakpoints } from 'common/views/appBreakpoints';
import { createLogger } from 'common/logger';
import { observer } from 'mobx-react-lite';

import { BackfaceMaterialBack, BackfaceMaterialFront, FrontLeftMaterial, FrontRightMaterial } from './common/materials';
import { ValueModel } from '@zajno/common-mobx/viewModels';
import { GLTFData } from 'scripts/animations/three/types/gltf';
import { useGLTF } from '@react-three/drei';

import ModelUrl from 'assets/gl/home/hero/graph.glb?url';

import VideoLeft from 'assets/video/home-hero/hero_chart_left.mp4';
import VideoLeftTabletMp4 from 'assets/video/home-hero/hero_chart_tablet_left.mp4';
// import VideoLeftTabletWebm from 'assets/video/home-hero/hero_chart_tablet_left.webm';
import VideoLeftMobileMp4 from 'assets/video/home-hero/hero_chart_mobile_left.mp4';
// import VideoLeftMobileWebm from 'assets/video/home-hero/hero_chart_mobile_left.webm';
import VideoLeftPlaceholder from 'assets/img/home-hero/home-hero-chart-left-placeholder.png';

import VideoRight from 'assets/video/home-hero/hero_chart_right.mp4';
import VideoRightTabletMp4 from 'assets/video/home-hero/hero_chart_tablet_right.mp4';
// import VideoRightTabletWebm from 'assets/video/home-hero/hero_chart_tablet_right.webm';
import VideoRightMobileMp4 from 'assets/video/home-hero/hero_chart_mobile_right.mp4';
// import VideoRightMobileWebm from 'assets/video/home-hero/hero_chart_mobile_right.webm';
import VideoRightPlaceholder from 'assets/img/home-hero/home-hero-chart-right-placeholder.png';


type GraphProps = JSX.IntrinsicElements['group'] & {
    // nothing here yet!
};

const LeftVideoSrc: Record<BreakpointType, SrcSetArgs> = {
    [BreakpointType.Desktop]: VideoLeft,
    [BreakpointType.Tablet]: [
        // { src: VideoLeftTabletWebm, type: 'video/webm' },
        { src: VideoLeftTabletMp4, type: 'video/mp4' },
    ],
    [BreakpointType.Mobile]: [
        // { src: VideoLeftMobileWebm, type: 'video/webm' },
        { src: VideoLeftMobileMp4, type: 'video/mp4' },
    ],
};

const RightVideoSrc: Record<BreakpointType, SrcSetArgs> = {
    [BreakpointType.Desktop]: VideoRight,
    [BreakpointType.Tablet]: [
        // { src: VideoRightTabletWebm, type: 'video/webm' },
        { src: VideoRightTabletMp4, type: 'video/mp4' },
    ],
    [BreakpointType.Mobile]: [
        // { src: VideoRightMobileWebm, type: 'video/webm' },
        { src: VideoRightMobileMp4, type: 'video/mp4' },
    ],
};

export const GlobalFallbackOverride = new ValueModel<{ left: THREE.MeshBasicMaterial, right: THREE.MeshBasicMaterial }>(null);

const LoadSettings = {
    muted: true,
    loop: true,
    start: false,
    priority: 0,
    unsuspend: Breakpoints.isDesktop
        ? 'canplay' as const
        : 'loadedmetadata' as const,
};

const LeftVideoLoader = createLazyVideoTexture({
    ...LoadSettings,
    key: 'home-hero-video-left',
    srcset: () => LeftVideoSrc[Breakpoints.Current.breakpoint.name],
    fallbackImageSrc: VideoLeftPlaceholder,
    logger: createLogger('[LeftVideoLoader]'),
    material: FrontLeftMaterial,
});

const RightVideoLoader = createLazyVideoTexture({
    ...LoadSettings,
    key: 'home-hero-video-right',
    srcset: () => RightVideoSrc[Breakpoints.Current.breakpoint.name],
    fallbackImageSrc: VideoRightPlaceholder,
    logger: createLogger('[RightVideoLoader]'),
    material: FrontRightMaterial,
});

type GraphGLTF = GLTFData<'left' | 'right' | 'left_2' | 'right_2' | 'left_3' | 'right_3'>;

export const Graph = observer((props: GraphProps) => {
    const videoLeft = LeftVideoLoader.useLazyLoader();
    const videoRight = RightVideoLoader.useLazyLoader();

    const model = useGLTF(ModelUrl) as GraphGLTF;
    const { nodes: {
        left, right,
        left_2, right_2,
        left_3, right_3,
    } } = model;

    const isGlobalOverridden = GlobalFallbackOverride.value !== null;
    const isPlaceholder = videoLeft.isPlaceholder || videoRight.isPlaceholder || isGlobalOverridden;

    useEffect(() => {
        if (isPlaceholder) {
            transaction(() => {
                Bridge.isVideoPlaceholder.setTrue();
                Bridge.isVideoLoaded.setFalse();
            });
            return;
        }

        Bridge.isVideoPlaceholder.setFalse();

        let initialized = false;

        // when we leave the tab, video is still playing in background, but animations are on RAF so they're paused
        // so we need to pause and restart everything manually
        const onVisibilityChange = () => {
            if (!initialized) {
                return;
            }
            Bridge.isVideoLoaded.setValue(!document.hidden);
        };

        // let some pause and do initial setup
        // if document is visible, it will start all animations
        setTimeout(() => {
            initialized = true;
            onVisibilityChange();
        }, 2000);

        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [isPlaceholder]);

    useEffect(() => {
        if (!videoLeft || !videoRight) {
            return;
        }

        return reaction(
            () => Bridge.isVideoPlaying.value,
            isPlaying => {
                if (isPlaying) {
                    videoLeft.safePlay();
                    videoRight.safePlay();
                } else {
                    videoLeft.stop();
                    videoRight.stop();
                }
            },
            { fireImmediately: true },
        );

    }, [videoLeft, videoRight]);

    const {
        left: LeftMaterial = FrontLeftMaterial,
        right: RightMaterial = FrontRightMaterial,
    } = GlobalFallbackOverride.value || {};

    return (
        <group
            name="graph"
            position={[0, -34, 200]}
            rotation={[-0.14, -0.25, -0.16]}
            scale={0.9}
            {...props}
        >
            <group name="graph_float" position={[0, 0, 0]}>
                <mesh
                    name="right 3"
                    geometry={right_3.geometry}
                    material={BackfaceMaterialFront}
                    position={[-172, 191.5, -3.5]}
                />
                <mesh
                    name="right 2"
                    geometry={right_2.geometry}
                    material={BackfaceMaterialBack}
                    position={[-172, 191.5, -7]}
                />
                <mesh
                    name="right"
                    geometry={right.geometry}
                    material={RightMaterial.map ? RightMaterial : BackfaceMaterialFront}
                    position={[-172, 191.5, 0]}
                />
                <mesh
                    name="left 3"
                    geometry={left_3.geometry}
                    material={BackfaceMaterialFront}
                    position={[-279, 191.5, -3.5]}
                />
                <mesh
                    name="left 2"
                    geometry={left_2.geometry}
                    material={BackfaceMaterialBack}
                    position={[-279, 191.5, -7]}
                />
                <mesh
                    name="left"
                    geometry={left.geometry}
                    material={LeftMaterial.map ? LeftMaterial : BackfaceMaterialFront}
                    position={[-279, 191.5, 0.01]}
                />
            </group>
        </group>
    );
});

export const preloader = () => useGLTF.preload(ModelUrl);
